import { type CSSProperties, useMemo } from 'react';
import { EMAIL_MAX_LENGTH } from '../emailRules';
import type { EmailCheck } from './useEmailCheck';

/**
 * autocomplete 값 — 브라우저가 **모르는 값**이어야 하고, 휴리스틱이 "이메일 칸" 으로 보지 않게
 * `email`·`mail` 을 넣지 않는다(자동 채우기 차단 이유는 아래 input 주석).
 */
const NEW_ADDRESS_AUTOCOMPLETE = 'rs-new-login-id';
/** name 접두사 — 뒤에 칸을 띄울 때마다 새 난수를 붙인다(`useFreshFieldName`). */
const NEW_ADDRESS_NAME_PREFIX = 'rs-new-login-id-';

/**
 * 칸을 띄울 때마다 새로 만드는 name.
 * 🔴 Chrome 의 **입력 기록**(전에 친 값 다시 보여 주기)은 칸 name 으로 묶인다 — autocomplete 가 off 가 아니면 켜진다.
 *   name 을 고정했더니 [조직 만들기] 에서 친 주소가 [사용자 추가] 칸에 "기존 이메일" 로 떴다(2026-09-29 사용자 지적).
 *   매번 다른 name 이면 맞는 기록이 없어 아무것도 뜨지 않는다.
 */
function useFreshFieldName(): string {
  return useMemo(() => NEW_ADDRESS_NAME_PREFIX + Math.random().toString(36).slice(2, 10), []);
}

/**
 * 이메일 입력칸 + [중복 확인] 버튼 + 결과 문구(2026-09-29 사용자 요청).
 * 이메일(= 로그인 ID)을 **새로 정하는** 세 화면이 같이 쓴다: 관리자 [사용자 추가] · 수퍼유저 [조직 만들기] · [내 이메일 변경].
 *
 * - 형식이 틀리면 칸을 떠날 때(또는 확인을 누를 때) "이메일 형식이 아닙니다." — 치는 도중에는 띄우지 않는다.
 * - 중복은 **버튼을 눌러야** 확인한다(자동 조회 아님 — 사용자 결정). Enter 도 확인이다(저장이 아니다 — 확인 전에 저장되지 않게).
 * - 저장 가능 여부는 부모가 `emailCheck.view.confirmed` 로 정한다(저장 버튼 disabled).
 *
 * @param value       입력값(부모 상태)
 * @param onChange    입력 변경
 * @param emailCheck  `useEmailCheck(api, value)` 결과
 * @param placeholder 칸 안내
 * @param autoFocus   열자마자 칸에 커서
 * @param inputStyle  칸 폭 등(인라인 배치 화면용)
 * @param ariaLabel   라벨이 따로 없는 화면(내 이메일 변경)용
 */
export function EmailCheckField({
  value,
  onChange,
  emailCheck,
  placeholder,
  autoFocus,
  inputStyle,
  ariaLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  emailCheck: EmailCheck;
  placeholder?: string;
  autoFocus?: boolean;
  inputStyle?: CSSProperties;
  ariaLabel?: string;
}) {
  const { view, check, touch } = emailCheck;
  const fieldName = useFreshFieldName();
  return (
    <div className="email-check">
      <div className="email-check-row">
        {/*
          🔴 브라우저 자동 채우기를 막는다(2026-09-29 사용자 지적 — su [조직 만들기] 에 Chrome 이 저장된 내 주소들을 띄웠다).
          이 칸은 **아직 아무도 안 쓰는 새 주소**를 적는 곳이라 "내 주소" 제안은 틀린 답을 권하는 셈이다.
          Chrome·Safari 는 `type="email"`·name·라벨 글자("이메일")로 주소 칸을 알아보고 **주소록 채우기에선 `autocomplete="off"` 를 무시**한다
          → 종류는 text(휴대폰 키보드는 inputMode 로 이메일 유지) + name 에서 email 을 빼고, autocomplete 에는 브라우저가
          **모르는 값**을 준다(모르는 값이 붙은 칸은 자동 채우기 대상에서 빠진다 — off 보다 잘 먹는 알려진 방법).
          그 대신 입력 기록이 켜지므로 name 은 띄울 때마다 새 값(`useFreshFieldName`).
          로그인 칸(EmailSuggestField)은 반대로 자동 채우기가 맞으므로 건드리지 않는다.
        */}
        <input
          type="text"
          inputMode="email"
          name={fieldName}
          autoComplete={NEW_ADDRESS_AUTOCOMPLETE}
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          placeholder={placeholder}
          value={value}
          maxLength={EMAIL_MAX_LENGTH}
          autoFocus={autoFocus}
          style={inputStyle}
          aria-label={ariaLabel}
          aria-invalid={view.tone === 'err' && !!view.message}
          onChange={(e) => onChange(e.target.value)}
          onBlur={touch}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (view.canCheck) void check();
            }
          }}
        />
        <button type="button" className="ghost" onClick={() => void check()} disabled={!view.canCheck}>
          중복 확인
        </button>
      </div>
      {view.message && (
        <div className={`email-check-msg ${view.tone}`} role={view.tone === 'err' ? 'alert' : 'status'}>
          {view.tone === 'ok' ? '✓ ' : ''}
          {view.message}
        </div>
      )}
    </div>
  );
}
